<template>
  <div
    class="table-row"
    :class="{
      'is-selected': isSelected,
      'even-row': game.board % 2 === 0,
      'odd-row': game.board % 2 === 1,
    }"
    @click="handleRowClick"
  >
    <!-- BOARD -->
    <div class="table-cell">
      <router-link
        v-if="game.matchRef && game.matchGUID"
        :to="getTournamentGameUrl(tournament.id, game.matchGUID)"
      >
        {{ game.board }}
      </router-link>
      <span v-else>{{ game.board }}</span>
    </div>

    <!-- WHITE PLAYER -->
    <div
      class="table-cell drop-zone"
      :class="{ 'drop-zone-active': isDragOverWhite }"
      @dragover="handleDragOver"
      @dragenter="handleDragEnterWhite"
      @dragleave="handleDragLeaveWhite"
      @drop="handleDropWhite"
    >
      <div
        v-if="game.whiteUsername"
        class="player-cell"
        :class="{ 'result-selector-open': showResultSelectors }"
      >
        <TournamentPlayerTile
          v-if="game.whiteUsername"
          :standing="whitePlayerStanding"
          :fullPlayer="findPlayer(game.whiteUsername)"
          :selectedUsername="null"
          :isAbsent="false"
          :isPlayed="false"
          :isPaired="true"
          :colorPref="whitePlayerColorPref"
          :isDraggable="true"
          :dragDataType="'game-player'"
          :gameId="game.gameId"
          :gameResult="game.whiteResult"
          :color="'white'"
          :canClose="true"
          :totalRounds="tournament.totalRounds"
          :roundNumber="round.roundNumber"
          @player-selected="() => {}"
          @close="$emit('set-players', game, null, game.blackUsername)"
          class="game-player-tile"
        />

        <TournamentResultSelect
          v-if="showResultSelectors"
          ref="whiteResultSelect"
          :result="game.whiteResult"
          :board-number="game.board"
          color="white"
          :is-two-player-match="!!(game.whiteUsername && game.blackUsername)"
          @update:result="
            (val) => {
              autoCompleteResult(val, null);
              handleRowClick();
            }
          "
        />
      </div>
      <div v-else>
        <span class="drop-hint">Drop here for white or </span>
        <TournamentPairingPlayerSelect
          :available-unpaired-players="availableUnpairedPlayers"
          :board-number="game.board"
          :color="'white'"
          :opponent-player="findPlayer(game.blackUsername)"
          :games="roundGames"
          :round-number="round.roundNumber"
          @update:player="
            (value) => $emit('set-players', game, value, game.blackUsername)
          "
        />
      </div>
    </div>

    <!-- SWAP BUTTON or RESULT -->
    <div class="table-cell">
      <TournamentGameResult
        v-if="
          hasGameResult ||
          (!showResultSelectors && (game.whiteUsername || game.blackUsername))
        "
        :game="game"
        @update:white="(val) => autoCompleteResult(val, null)"
        @update:black="(val) => autoCompleteResult(null, val)"
      />

      <ButtonTooltip
        v-else-if="showResultSelectors"
        v-show="game.whiteUsername && game.blackUsername"
        tooltip="Swap player positions"
        icon-left="retweet"
        type="is-light"
        size="is-small"
        outlined
        @click="
          $emit('set-players', game, game.blackUsername, game.whiteUsername)
        "
        :disabled="game.matchRef"
      />
    </div>

    <!-- BLACK PLAYER -->
    <div
      class="table-cell drop-zone"
      :class="{ 'drop-zone-active': isDragOverBlack }"
      @dragover="handleDragOver"
      @dragenter="handleDragEnterBlack"
      @dragleave="handleDragLeaveBlack"
      @drop="handleDropBlack"
    >
      <div
        v-if="game.blackUsername"
        class="player-cell"
        :class="{ 'result-selector-open': showResultSelectors }"
      >
        <TournamentResultSelect
          v-if="showResultSelectors"
          ref="blackResultSelect"
          :result="game.blackResult"
          :board-number="game.board"
          color="black"
          :is-two-player-match="!!(game.whiteUsername && game.blackUsername)"
          @update:result="
            (val) => {
              autoCompleteResult(null, val);
              handleRowClick();
            }
          "
        />

        <TournamentPlayerTile
          v-if="game.blackUsername"
          :standing="blackPlayerStanding"
          :fullPlayer="findPlayer(game.blackUsername)"
          :selectedUsername="null"
          :isAbsent="false"
          :isPlayed="false"
          :isPaired="true"
          :colorPref="blackPlayerColorPref"
          :isDraggable="true"
          :dragDataType="'game-player'"
          :gameId="game.gameId"
          :gameResult="game.blackResult"
          :color="'black'"
          :canClose="true"
          :totalRounds="tournament.totalRounds"
          :roundNumber="round.roundNumber"
          @player-selected="() => {}"
          @close="$emit('set-players', game, game.whiteUsername, null)"
          class="game-player-tile"
        />
      </div>
      <div v-else class="empty-slot">
        <span class="drop-hint">Drop here for black or </span>
        <TournamentPairingPlayerSelect
          :available-unpaired-players="availableUnpairedPlayers"
          :board-number="game.board"
          :color="'black'"
          :opponent-player="findPlayer(game.whiteUsername)"
          :games="roundGames"
          :round-number="round.roundNumber"
          @update:player="
            (value) => $emit('set-players', game, game.whiteUsername, value)
          "
        />
      </div>
    </div>

    <!-- STATUS -->
    <div
      class="table-cell"
      @mouseover="$emit('hover', game.matchRef ? game.matchRef.match_id : null)"
      @mouseleave="$emit('hover', null)"
    >
      <div class="mr-2">
        <!-- Show unsaved changes status with tooltip -->
        <b-tooltip
          v-if="hasUnsavedChanges"
          :label="unsavedChangesTooltip"
          :position="game.board === 1 ? 'is-bottom' : 'is-top'"
          multilined
          type="is-warning"
        >
          <span class="has-text-warning"> ⚠️ </span>
        </b-tooltip>

        <!-- Normal status display -->
        <span
          v-if="game.matchRef"
          :class="
            hasResultMismatch(game)
              ? 'has-text-danger'
              : hasPlayerMismatch(game)
              ? 'has-background-warning'
              : 'has-text-info'
          "
        >
          <span v-show="hoverId != game.matchRef.match_id">
            {{ game.matchRef | matchResultText }}
          </span>
          <button
            v-show="hoverId === game.matchRef.match_id"
            @click="$emit('rematch', game)"
          >
            Rematch
          </button>
        </span>
        <span
          v-else-if="
            game.blackUsername &&
            game.whiteUsername &&
            !game.blackResult &&
            !game.whiteResult
          "
        >
          <button @click="$emit('start-game', game)">Start</button>
        </span>
        <span v-else-if="!game.blackUsername && !game.whiteUsername">
          Empty
        </span>
        <span v-else-if="!isOfflineClassTournament">no match</span>
        <span
          v-else-if="
            game.blackUsername &&
            game.whiteUsername &&
            game.blackResult !== TOURNAMENT_GAME_RESULT.UNFINISHED &&
            game.whiteResult !== TOURNAMENT_GAME_RESULT.UNFINISHED
          "
        >
          Finished
        </span>
        <span v-else>Ready</span>
      </div>

      <!-- Tournament Warnings -->
      <TournamentGameWarnings
        :game="game"
        :all-games="allGames"
        :round="round"
        :find-player="findPlayer"
      />
    </div>
  </div>
</template>

<script>
import TournamentPlayerTile from "@/components/admins/tournaments/TournamentPlayerTile.vue";
import ButtonTooltip from "@/components/ui/ButtonTooltip.vue";
import TournamentResultSelect from "@/components/admins/TournamentResultSelect.vue";
import TournamentPairingPlayerSelect from "@/components/admins/TournamentPairingPlayerSelect.vue";
import TournamentGameWarnings from "@/components/tournaments/TournamentGameWarnings.vue";
import TournamentGameResult from "@/components/tournaments/TournamentGameResult.vue";
import gameUrls from "@/mixins/gameUrls";
import GameHelper from "@/utils/game-helper";
import {
  hasResultMismatch,
  hasUnsavedChanges,
} from "vue-chess-shared/tournament/utils/gameUtils";
import { getSmartAutoCompletedResults } from "vue-chess-shared/tournament/utils/resultUtils";
import {
  TOURNAMENT_GAME_RESULT,
  TOURNAMENT_GAME_RESULT_TEXT,
} from "vue-chess-shared/constants";

import { createCompletePlayerStanding } from "@/utils/tournament-player-utils";
import { getColorPref } from "vue-chess-shared/tournament/utils/playerUtils";

export default {
  name: "TournamentGameRow",

  components: {
    TournamentPlayerTile,
    ButtonTooltip,
    TournamentResultSelect,
    TournamentPairingPlayerSelect,
    TournamentGameWarnings,
    TournamentGameResult,
  },

  mixins: [gameUrls],

  props: {
    game: {
      type: Object,
      required: true,
    },
    tournament: {
      type: Object,
      required: true,
    },
    round: {
      type: Object,
      required: true,
    },
    roundGames: {
      type: Array,
      required: true,
    },
    allGames: {
      type: Array,
      default: () => [],
    },
    availableUnpairedPlayers: {
      type: Array,
      required: true,
    },
    hoverId: {
      type: [String, Number, null],
      default: null,
    },
    findPlayer: {
      type: Function,
      required: true,
    },
    isOfflineClassTournament: {
      type: Boolean,
      default: false,
    },
    savedGame: {
      type: Object,
      default: null,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    "hover",
    "rematch",
    "start-game",
    "set-players",
    "set-result",
    "game-selected",
  ],

  data() {
    return {
      isDragOverWhite: false,
      isDragOverBlack: false,
      TOURNAMENT_GAME_RESULT,
    };
  },

  computed: {
    /**
     * Check if this game has unsaved changes using shared utility
     */
    hasUnsavedChanges() {
      return hasUnsavedChanges(this.game, this.savedGame);
    },

    showResultSelectors() {
      return this.isSelected;
    },

    hasGameResult() {
      const hasWhiteResult =
        this.game.whiteResult != null &&
        this.game.whiteResult !== TOURNAMENT_GAME_RESULT.UNFINISHED;
      const hasBlackResult =
        this.game.blackResult != null &&
        this.game.blackResult !== TOURNAMENT_GAME_RESULT.UNFINISHED;
      const hasResult = hasWhiteResult || hasBlackResult;
      return hasResult;
    },

    /**
     * Get complete standing data for white player
     */
    whitePlayerStanding() {
      if (!this.game.whiteUsername) return null;
      const player = this.findPlayer(this.game.whiteUsername);
      return this.createStandingForPlayer(player);
    },

    /**
     * Get complete standing data for black player
     */
    blackPlayerStanding() {
      if (!this.game.blackUsername) return null;
      const player = this.findPlayer(this.game.blackUsername);
      return this.createStandingForPlayer(player);
    },

    /**
     * Get color preference for white player
     */
    whitePlayerColorPref() {
      return this.whitePlayerStanding && this.whitePlayerStanding.colorPref
        ? this.whitePlayerStanding.colorPref
        : "+0";
    },

    /**
     * Get color preference for black player
     */
    blackPlayerColorPref() {
      return this.blackPlayerStanding && this.blackPlayerStanding.colorPref
        ? this.blackPlayerStanding.colorPref
        : "+0";
    },

    /**
     * Get list of changed fields for tooltip - only shows fields that actually changed
     */
    changedFields() {
      if (!this.savedGame || !this.hasUnsavedChanges) return [];

      const changes = [];
      const fieldsToCheck = [
        { key: "whiteUsername", label: "White_Player" },
        { key: "blackUsername", label: "Black_Player" },
        { key: "whiteResult", label: "White_Result" },
        { key: "blackResult", label: "Black_Result" },
        { key: "whiteScoreBeforeGame", label: "White_Score_Before" },
        { key: "blackScoreBeforeGame", label: "Black_Score_Before" },
      ];

      // Only include fields that actually changed
      fieldsToCheck.forEach((field) => {
        if (this.game[field.key] != this.savedGame[field.key]) {
          const oldValue = this.formatFieldValue(
            this.savedGame[field.key],
            field.key
          );
          const newValue = this.formatFieldValue(
            this.game[field.key],
            field.key
          );
          changes.push(`${field.label}:${oldValue}→${newValue}`);
        }
      });

      return changes;
    },

    /**
     * Get tooltip content for unsaved changes
     */
    unsavedChangesTooltip() {
      if (!this.hasUnsavedChanges) return "";

      const header = "⚠️ Unsaved Changes:";
      const changesList = this.changedFields.join("\n•");
      return `•••••${header}•••••\n•${changesList}`;
    },
  },

  methods: {
    hasResultMismatch(game) {
      return hasResultMismatch(game, game.matchRef);
    },

    hasPlayerMismatch(game) {
      return GameHelper.hasPlayerMismatch(game);
    },

    autoCompleteResult(whiteResult, blackResult) {
      const { autoWhiteResult, autoBlackResult } = getSmartAutoCompletedResults(
        this.game,
        whiteResult,
        blackResult
      );
      this.$emit("set-result", this.game, autoWhiteResult, autoBlackResult);
    },

    /**
     * Format field values for display in tooltip
     */
    formatFieldValue(value, fieldKey) {
      if (value === null || value === undefined) {
        return "empty";
      }

      // Format result values to be more readable
      if (fieldKey.includes("Result")) {
        return TOURNAMENT_GAME_RESULT_TEXT[value] || value;
      }

      return value;
    },

    // Drag and Drop Methods
    handleDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },

    handleDragEnterWhite(event) {
      event.preventDefault();
      this.isDragOverWhite = true;
    },

    handleDragLeaveWhite(event) {
      // Only remove highlight if we're actually leaving the drop zone
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isDragOverWhite = false;
      }
    },

    handleDragEnterBlack(event) {
      event.preventDefault();
      this.isDragOverBlack = true;
    },

    handleDragLeaveBlack(event) {
      // Only remove highlight if we're actually leaving the drop zone
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isDragOverBlack = false;
      }
    },

    handleDropWhite(event) {
      event.preventDefault();
      this.isDragOverWhite = false;

      try {
        const dragData = JSON.parse(event.dataTransfer.getData("text/plain"));

        if (dragData.type === "player") {
          // Handle drag from tile container
          this.removePlayerFromAllGames(dragData.username);
          this.$emit(
            "set-players",
            this.game,
            dragData.username,
            this.game.blackUsername
          );
        } else if (dragData.type === "game-player") {
          // Handle drag from another game cell
          this.removePlayerFromAllGames(dragData.username);
          this.$emit(
            "set-players",
            this.game,
            dragData.username,
            this.game.blackUsername
          );
        }
      } catch (error) {
        console.error("Error parsing drag data:", error);
      }
    },

    handleDropBlack(event) {
      event.preventDefault();
      this.isDragOverBlack = false;

      try {
        const dragData = JSON.parse(event.dataTransfer.getData("text/plain"));

        if (dragData.type === "player") {
          // Handle drag from tile container
          this.removePlayerFromAllGames(dragData.username);
          this.$emit(
            "set-players",
            this.game,
            this.game.whiteUsername,
            dragData.username
          );
        } else if (dragData.type === "game-player") {
          // Handle drag from another game cell
          this.removePlayerFromAllGames(dragData.username);
          this.$emit(
            "set-players",
            this.game,
            this.game.whiteUsername,
            dragData.username
          );
        }
      } catch (error) {
        console.error("Error parsing drag data:", error);
      }
    },

    /**
     * Handle row click to select/deselect the game
     */
    handleRowClick() {
      this.$emit("game-selected", this.game.gameId);
    },

    /**
     * Create a standing object for TournamentPlayerTile
     */
    createStandingForPlayer(player) {
      if (!player) return null;

      return createCompletePlayerStanding(
        player,
        this.round,
        this.tournament.players || [],
        getColorPref
      );
    },

    /**
     * Remove a player from ALL games where they're currently assigned
     */
    removePlayerFromAllGames(username) {
      // Find all games where this player is currently assigned
      const gamesWithPlayer = this.roundGames.filter(
        (game) =>
          game.whiteUsername === username || game.blackUsername === username
      );

      // Remove the player from each game they're in
      gamesWithPlayer.forEach((game) => {
        const newWhite =
          game.whiteUsername === username ? null : game.whiteUsername;
        const newBlack =
          game.blackUsername === username ? null : game.blackUsername;

        this.$emit("set-players", game, newWhite, newBlack);
      });
    },
  },
};
</script>

<style scoped>
.table-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #dbdbdb;
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 0;
}

.table-row.is-selected {
  background-color: #2196f3 !important;
  border-left: 5px solid #1976d2;
  border-right: 7px solid #1976d2;
}

/* Drop zone styling */
.drop-zone {
  position: relative;
  transition: background-color 0.2s ease;
}

.drop-zone-active {
  background-color: #e8f5e8;
  border: 2px dashed #4caf50;
}

.drop-hint {
  font-size: 0.85rem;
  color: #ccc;
}

.drop-zone-active .drop-hint {
  color: #4caf50;
  font-weight: bold;
}

.table-cell {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  min-height: 70px; /* Ensure cells are tall enough for tiles */
}

/* Match the original table column widths */
.table-cell:nth-child(1) {
  width: 5%;
} /* Board */
.table-cell:nth-child(2) {
  width: 35%;
} /* White */
.table-cell:nth-child(3) {
  width: 10%;
} /* Swap */
.table-cell:nth-child(4) {
  width: 35%;
} /* Black */
.table-cell:nth-child(5) {
  width: 15%;
} /* Status */

/* Alternating row colors matching TournamentTableGames */
.table-row.even-row {
  background-color: #740074;
  color: white;
}

.table-row.odd-row {
  background-color: #573e97;
  color: white;
}

/* Highlight rows with unsaved changes */
.table-row.has-unsaved-changes {
  background-color: #8b6914 !important;
  border-left: 4px solid #ffc107;
  color: white;
}

.table-row.has-unsaved-changes:hover {
  background-color: #a67c1a !important;
  color: white;
}

.player-cell {
  display: flex;
  justify-content: center;
  width: 100%;
}

.result-selector-open {
  justify-content: space-between;
}
</style>
